'use client'

import React from 'react'
import type { ImageProps } from 'next/image'
import Image from 'next/image'
import blakeLogo from '../assets/blake-elearning-logo-red.png'
import threePLearningLogo from '../assets/3p-logo.jpg'
import facebookLogo from '../assets/social_icons/facebook@2x.png'
import instagramLogo from '../assets/social_icons/instagram@2x.png'
import linkedinLogo from '../assets/social_icons/linkedin@2x.png'
import pinterestLogo from '../assets/social_icons/pinterest@2x.png'
import tiktokLogo from '../assets/social_icons/tiktok@2x.png'
import twitterLogo from '../assets/social_icons/twitter@2x.png'
import youtubeLogo from '../assets/social_icons/youtube@2x.png'
import kidsafeLogo from '../assets/kidsafe-logo.png'
import { Container } from './Container'
import { NetReviewsWidgetSimple } from './NetReviewsWidgetSimple'
import { siteLocale, Locale } from '@marketing-sites/utils'

const currentYear = String(new Date().getFullYear())

interface footerLinksType {
  text: string
  href: string
  newTab?: boolean
  Component?: React.FC
  showCondition?: boolean
}

const footerLinks: footerLinksType[] = [
  {
    text: `© ${currentYear}`,
    href: '/copyright/',
  },
  {
    text: 'About Us',
    href: '/about-blake/',
  },
  {
    text: 'About 3P Learning',
    href: '/about-3p-learning/',
  },
  {
    text: 'Terms of Use',
    href: '/terms/',
  },
  {
    text: 'Privacy Policy',
    href: '/privacy/',
  },
  { text: '', href: '', Component: YourPrivacyChoices },
  {
    text: 'Refund Policy',
    href: '/refund/',
  },
  {
    text: 'Contact Us',
    href: '/contact-us/',
  },
  {
    text: 'Collaborate With Us',
    href: '/collaborate-with-us/',
  },
  {
    text: 'Accessibility',
    href: '/accessibility/',
  },
  {
    text: 'Do Not Sell My Info - US Residents Only',
    href: '/ccpa/',
    showCondition: siteLocale == Locale.us,
  },
  {
    text: 'Responsible Disclosure Program',
    href: '/responsible-disclosure-program/',
  },
  {
    text: 'Data Breach Response Plan',
    href: '/data-breach-plan/',
  },
  {
    text: 'Modern Slavery and Human Trafficking Statement',
    href: 'https://marketing-cdn.3plearning.com/uploads/assets/3P/2024-modern-slavery.pdf',
    newTab: true,
  },
]

interface SocialLinkProps {
  href: string
  alt: string
  imageSrc: ImageProps['src']
}

function SocialLink({ href, alt, imageSrc }: SocialLinkProps) {
  return (
    <a href={href} className="relative inline-block h-[30px] w-[30px]">
      <Image alt={alt} src={imageSrc} height={30} width={30} />
    </a>
  )
}

function FacebookLink() {
  return (
    <SocialLink
      href="https://www.facebook.com/readingeggs/"
      imageSrc={facebookLogo}
      alt="Facebook Page"
    />
  )
}

function InstagramLink() {
  return (
    <SocialLink
      href="https://www.instagram.com/readingeggs/"
      imageSrc={instagramLogo}
      alt="Instagram Profile"
    />
  )
}

function YoutubeLink() {
  return (
    <SocialLink
      href="https://www.youtube.com/c/readingeggs"
      imageSrc={youtubeLogo}
      alt="Youtube Channel"
    />
  )
}

function LinkedinLink() {
  return (
    <SocialLink
      href="https://www.linkedin.com/company/blake-elearning/"
      imageSrc={linkedinLogo}
      alt="LinkedIn Profile"
    />
  )
}

function PinterestLink() {
  return (
    <SocialLink
      href="https://www.pinterest.com.au/readingeggs/"
      imageSrc={pinterestLogo}
      alt="Pinterest Profile"
    />
  )
}

function TwitterLink() {
  return (
    <SocialLink
      href="https://twitter.com/readingeggs"
      imageSrc={twitterLogo}
      alt="Twitter Feed"
    />
  )
}

function TikTokLink() {
  return (
    <SocialLink
      href="https://www.tiktok.com/@reading_eggs"
      imageSrc={tiktokLogo}
      alt="TikTok Profile"
    />
  )
}

export function ReadingEggsFooter() {
  return (
    <footer className="border-t border-[#a5ce42] bg-[#b3d23d] bg-gradient-to-b from-[#cefd40] via-[#b1d34a] via-[12%] to-[#b3d23d] p-8">
      <Container className="flex flex-col lg:flex-row lg:space-x-8 lg:pb-20">
        <>
          <nav
            role="navigation"
            aria-label="Company"
            className="h-auto lg:w-1/4"
          >
            <ul className="flex flex-col items-center justify-center space-y-4">
              <li>
                <a href="https://blake.com.au/">
                  <Image
                    src={blakeLogo}
                    alt="Blake eLearning Home"
                    className="h-auto max-w-full"
                  />
                </a>
              </li>
              <li>
                <a href="https://www.3plearning.com/">
                  <Image
                    src={threePLearningLogo}
                    alt="3P Learning Home"
                    className="h-auto max-w-full"
                  />
                </a>
              </li>
            </ul>
          </nav>

          <nav
            role="navigation"
            aria-label="Legal and General Information"
            className="flex lg:w-3/4"
          >
            <ul className="flex h-fit flex-wrap items-center justify-center space-x-4 py-4 lg:py-0">
              {footerLinks
                .filter((link) => link.showCondition ?? true)
                .map((link) => (
                  <li key={link.text} className="mb-2">
                    {link.Component ? (
                      <link.Component />
                    ) : (
                      <a
                        className="text-sm text-white hover:text-white hover:underline focus:text-white focus:underline"
                        href={link.href}
                        target={link.newTab ? '_blank' : '_self'}
                      >
                        {link.text}
                      </a>
                    )}
                  </li>
                ))}
            </ul>
          </nav>

          <div className="flex flex-col items-center lg:w-1/4">
            <div className="relative mb-4">
              <NetReviewsWidgetSimple />
            </div>

            <nav role="navigation" aria-label="Social Network">
              <ul className="flex space-x-2">
                <li>
                  <InstagramLink />
                </li>
                <li>
                  <FacebookLink />
                </li>
                <li>
                  <YoutubeLink />
                </li>
                <li>
                  <TwitterLink />
                </li>
                <li>
                  <PinterestLink />
                </li>
                <li>
                  <LinkedinLink />
                </li>
                <li>
                  <TikTokLink />
                </li>
              </ul>
            </nav>

            <div className="my-4 flex justify-center">
              <a
                href="https://www.kidsafeseal.com/certifiedproducts/Readingeggs_websites.html"
                target="_blank"
                rel="noreferrer"
              >
                <Image
                  alt="Reading Eggs Websites are certified by the kidSAFE Seal Program."
                  src={kidsafeLogo}
                  className="h-auto max-w-full"
                  sizes="100vw"
                />
              </a>
            </div>
          </div>
        </>
      </Container>
    </footer>
  )
}

declare global {
  interface Window {
    UC_UI: { showSecondLayer: () => void }
  }
}

function YourPrivacyChoices() {
  function openUsercentrics() {
    try {
      window.UC_UI.showSecondLayer()
    } catch {
      alert('An error occurred, please try again shortly.')
    }
  }

  return (
    <div
      className="flex shrink-0 cursor-pointer items-center gap-1"
      onClick={() => {
        openUsercentrics()
      }}
    >
      <svg
        style={{
          verticalAlign: 'middle',
          filter: 'drop-shadow(0 0 1px rgba(0, 0, 0, 0.3))',
        }}
        width="28"
        height="16"
        viewBox="0 0 28 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect width="28" height="16" rx="8" fill="#36ADE1"></rect>
        <rect x="14" y="2" width="12" height="12" rx="6" fill="white"></rect>
      </svg>
      <span className="text-sm text-white hover:text-white hover:underline focus:text-white focus:underline">
        Your Privacy Choices
      </span>
    </div>
  )
}
